import {Form, Formik, FormikProps} from 'formik';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    Button,
    DataWrapper,
    PasswordInputFormik,
    ToggleFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {ValidationCode} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {SipTwilioSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const FIELDS = {
    ENABLED: 'enabled',
    APPLICATION_SID: 'applicationSid',
    DOMAIN_SID: 'domainSid',
    VIRTUAL_AGENT_ENABLED: 'virtualAgent.enabled',
    VOICE_AGENT_ENABLED: 'voiceAgent.enabled',
    REDIRECT_ENABLED: 'redirect.enabled',
    REDIRECT_FLOW_WEBHOOK_URL: 'redirect.flowWebhookUrl',
};

const VOICE_AGENT_PROVIDER = 'internal-live-agent';

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<SipTwilioSettings>;

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.SIP.TWILIO_PROPERTIES;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FormTemplate: React.FC<FormTemplateProps> = ({loading, initialValues, values, isValid, submitForm}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-my-2' defaultExpanded>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-ts-h5-bold'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-max-w-104'>
                            <ToggleFormik
                                name={FIELDS.ENABLED}
                                label={t(T_KEYS.LABELS.ENABLED)}
                                className='tw-my-4'
                            />
                            <PasswordInputFormik
                                name={FIELDS.APPLICATION_SID}
                                label={t(T_KEYS.LABELS.APPLICATION_SID)}
                                className='tw-w-full'
                                spacing='bottom'
                                autoComplete='one-time-code'
                                required={values?.enabled}
                            />
                            <PasswordInputFormik
                                name={FIELDS.DOMAIN_SID}
                                label={t(T_KEYS.LABELS.DOMAIN_SID)}
                                className='tw-w-full'
                                spacing='bottom'
                                autoComplete='one-time-code'
                            />
                            <ToggleFormik
                                name={FIELDS.VIRTUAL_AGENT_ENABLED}
                                label={t(T_KEYS.LABELS.VIRTUAL_AGENT_ENABLED)}
                                className='tw-my-4'
                            />
                            <div>
                                <ToggleFormik
                                    name={FIELDS.VOICE_AGENT_ENABLED}
                                    label={t(T_KEYS.LABELS.VOICE_AGENT_ENABLED)}
                                    className='tw-my-4'
                                />
                            </div>
                            <div>
                                <ToggleFormik
                                    name={FIELDS.REDIRECT_ENABLED}
                                    label={t(T_KEYS.LABELS.REDIRECT)}
                                    className='tw-my-4'
                                />
                                <PasswordInputFormik
                                    name={FIELDS.REDIRECT_FLOW_WEBHOOK_URL}
                                    label={t(T_KEYS.LABELS.FLOW_WEBHOOK_URL)}
                                    className='tw-w-full'
                                    spacing='bottom'
                                    autoComplete='one-time-code'
                                    required={values?.redirect?.enabled}
                                />
                            </div>
                            <div className='tw-text-right tw-mt-controls'>
                                <Button
                                    color='primary'
                                    disabled={hasNoChanges || !isValid}
                                    loading={loading}
                                    onClick={submitForm}
                                >
                                    {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                                </Button>
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type TwilioPropertiesProps = {
    loading: boolean;
    initialValues: SipTwilioSettings;
    onSubmit: (values: SipTwilioSettings) => void;
};

export const TwilioProperties: React.FC<TwilioPropertiesProps> = ({
    loading, initialValues, onSubmit
}) => {
    const {t} = useTranslation();
    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.ENABLED]: Yup.boolean(),
        [FIELDS.APPLICATION_SID]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            }),
        redirect: Yup.object().shape({
            flowWebhookUrl: Yup.string().when('enabled', {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            })
        })
    }), [t]);

    const handleSubmit = useCallback((values: SipTwilioSettings) => {
        const voiceAgentEnabled = values.voiceAgent?.enabled;
        const normalized: SipTwilioSettings = {
            ...values,
            ...(voiceAgentEnabled && {
                voiceAgent: {
                    enabled: true,
                    provider: VOICE_AGENT_PROVIDER
                }
            })
        };
        onSubmit(normalized);
    }, [onSubmit]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
        >
            {(props) => <FormTemplate {...props} loading={loading} />}
        </Formik>
    );
};